import router from './router'
import { useAppStoreWithOut } from '@/store/modules/app'
import type { RouteRecordRaw } from 'vue-router'
import { useTitle } from '@/hooks/web/useTitle'
import { useNProgress } from '@/hooks/web/useNProgress'
import { usePermissionStoreWithOut } from '@/store/modules/permission'
import { usePageLoading } from '@/hooks/web/usePageLoading'
import { NO_REDIRECT_WHITE_LIST, SUCCESS_CODE } from '@/constants'
import { useUserStoreWithOut } from '@/store/modules/user'
import { addRedirectField } from '@/utils/routerHelper'
import { getUserRoleApi } from '@/api/login'
import { useStorage } from '@/hooks/web/useStorage'

const { setStorage } = useStorage()

const { start, done } = useNProgress()

const { loadStart, loadDone } = usePageLoading()

// 动态路由初始化锁，避免首次进入时并发导航重复请求 /auth/menu
let dynamicRouteInitPromise: Promise<void> | null = null

/** 将已生成的动态路由注册到 router 实例 */
const registerDynamicRoutes = () => {
  const permissionStore = usePermissionStoreWithOut()
  permissionStore.getAddRouters.forEach((route) => {
    router.addRoute(route as unknown as RouteRecordRaw)
  })
  permissionStore.setIsAddRouters(true)
}

/** 刷新后 pinia 已持久化 addRouters，但 router 实例需重新 addRoute */
const restoreDynamicRoutesFromCache = (): boolean => {
  const permissionStore = usePermissionStoreWithOut()
  if (permissionStore.getIsAddRouters) {
    return true
  }
  if (!permissionStore.addRouters?.length) {
    return false
  }
  registerDynamicRoutes()
  return true
}

/** 拉取菜单并生成动态路由；同一时刻仅执行一次 */
const ensureDynamicRoutes = async (forceRefresh = false): Promise<void> => {
  const permissionStore = usePermissionStoreWithOut()
  const appStore = useAppStoreWithOut()
  const userStore = useUserStoreWithOut()

  if (forceRefresh) {
    permissionStore.setIsAddRouters(false)
  }

  if (!forceRefresh && restoreDynamicRoutesFromCache()) {
    return
  }

  if (dynamicRouteInitPromise) {
    return dynamicRouteInitPromise
  }

  dynamicRouteInitPromise = (async () => {
    const res = await getUserRoleApi()
    if (!res || res.code !== SUCCESS_CODE || !res.data) {
      throw new Error(res?.message || '获取菜单失败')
    }

    setStorage('btnList', res.data.btnList)
    userStore.setUserData(res.data.account)
    userStore.setUserLoginExtend(res.data.loginExtend)
    const menu = Array.isArray(res.data)
      ? addRedirectField(res.data)
      : addRedirectField(res.data.menuList)

    if (appStore.getDynamicRouter) {
      appStore.serverDynamicRouter
        ? await permissionStore.generateRoutes('server', menu as AppCustomRouteRecordRaw[])
        : await permissionStore.generateRoutes('frontEnd', menu as string[])
    } else {
      await permissionStore.generateRoutes('static')
    }

    registerDynamicRoutes()
  })().finally(() => {
    dynamicRouteInitPromise = null
  })

  return dynamicRouteInitPromise
}

/** 动态路由就绪后，决定是原地 replace 还是跳转到 redirect 目标 */
const resolvePostInitNavigation = (to: typeof router.currentRoute.value, from: typeof router.currentRoute.value) => {
  const redirectPath = (from.query.redirect as string) || to.path
  const redirect = decodeURIComponent(redirectPath)
  return to.path === redirect ? { ...to, replace: true as const } : { path: redirect }
}

router.beforeEach(async (to, from, next) => {
  // 清除上次可能因导航中断而未配对的 loading，避免整页遮罩残留
  loadDone()
  start()
  const permissionStore = usePermissionStoreWithOut()
  const userStore = useUserStoreWithOut()

  // 仅以本地 JWT 判断登录态，避免残留 userInfo 触发无效权限请求。
  const hasToken = !!userStore.getToken
  const isLoggedIn = hasToken

  if (isLoggedIn) {
    if (to.path === '/login') {
      const redirectTarget = to.query.redirect
        ? decodeURIComponent(to.query.redirect as string)
        : '/'
      next({ path: redirectTarget, replace: true })
      return
    }

    if (permissionStore.getIsAddRouters) {
      next()
      return
    }

    try {
      loadStart()
      await ensureDynamicRoutes()
      next(resolvePostInitNavigation(to, from))
    } catch (error) {
      console.error('获取用户权限失败:', error, to)
      loadDone()
      done()
      // 权限请求失败时清理无效本地登录态，统一返回系统登录页。
      permissionStore.setIsAddRouters(false)
      localStorage.removeItem('userInfo')
      localStorage.removeItem('accessToken')
      localStorage.removeItem('refreshToken')
      userStore.setToken('')
      userStore.setRefreshToken('')
      next(`/login?redirect=${to.path}`)
    }
  } else {
    console.log('用户令牌丢失', userStore)
    if (NO_REDIRECT_WHITE_LIST.indexOf(to.path) !== -1) {
      next()
    } else {
      next(`/login?redirect=${to.path}`)
    }
  }
})

router.afterEach((to) => {
  useTitle(to?.meta?.title as string)
  done() // 结束Progress
  loadDone()
})

router.onError((error) => {
  console.error('路由错误:', error)
  done()
  loadDone()
})
